import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import LoadingPage from '../../../components/LoadingPage/LoadingPage';
import LangEnum from '../../../lang/LangEnum';
import snackbarStore from '../../../stores/Snackbar.store';
import NotFoundPage from '../../errors/pages/NotFoundPage/NotFoundPage';
import { useTranslation } from '../../translation/TranslationContext';
import { useSignatureContext } from '../contract/contexts/signature.context';
import { SignatureContextType } from '../contract/contexts/signature.context.types';
import { signedContractRepository } from '../contract/repository/signedContract.repository';
import { CompleteSignedContract } from '../contract/signed-contract';
import { ModalContractPage } from '../contract/types';

type InfoLoaderProps = {
  setOpen: (open: boolean) => void;
};

const getPageToGo = (signAs: SignatureContextType.SigningAs, signature: CompleteSignedContract) => {
  if (
    (signAs === 'BRAND' && signature.userBrandSignedAt) ||
    (signAs === 'STORE' && signature.userStoreSignedAt)
  ) {
    return ModalContractPage.CONTRACT_SIGNED;
  }

  return ModalContractPage.READ_AND_SIGN_CONTRACT;
};

const InfoLoader = ({ setOpen }: InfoLoaderProps) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const params = useParams<{ id: string }>();
  const { translate } = useTranslation();

  const {
    changePage,
    changeContractInfo,
    changeSigningAs,
    loadContractUrl,
    changeUnauthenticatedSign,
  } = useSignatureContext();

  useEffect(() => {
    if (!params.id) {
      return;
    }

    const openModal = () => {
      setLoading(false);
      setOpen(true);
    };

    const loadData = async () => {
      const response = await signedContractRepository().getContractInfoByExternalLink(params.id);

      if (response.ok && response.data) {
        const { signedContract, signAs } = response.data;

        // Updating the context of the signature modal
        changePage(getPageToGo(signAs, signedContract), signedContract);
        changeContractInfo({
          version: signedContract.version,
          createdAt: signedContract.createdAt,
          id: signedContract.id,
        });
        changeSigningAs(signAs);
        loadContractUrl(signedContract.url);
        changeUnauthenticatedSign(true);

        openModal();
      } else {
        snackbarStore.handleMessage({
          message: translate(LangEnum.ORDER_CONTRACT_EXTERNAL_LINK_ERROR),
          type: 'error',
        });
        setLoading(false);
        setError(true);
      }
    };

    loadData();
  }, [
    params.id,
    changePage,
    changeContractInfo,
    changeSigningAs,
    setOpen,
    loadContractUrl,
    changeUnauthenticatedSign,
    translate,
  ]);

  if (loading) {
    return <LoadingPage />;
  }

  if (error) {
    return (
      <NotFoundPage
        hasHomeAction={false}
        title={LangEnum.ORDER_CONTRACT_404_ERROR_TITLE}
        description={LangEnum.ORDER_CONTRACT_404_ERROR_MESSAGE}
      />
    );
  }

  return null;
};

export default InfoLoader;
